import { useState } from 'react';
import { useSnackbar } from 'notistack';
import useAuth from './useAuth';
import { donations, foodRun, rescues } from 'src/backend';
import SocketIO from 'src/backend/SocketIO';
import debouncedFunction from 'src/utils/debouncedFunction';

export default function useFoodRunStatus({
  data,
  getFoodRunData,
  handleCompleteSuccess,
  onCancel: _onCancel,
}) {
  const socket = SocketIO.getIO();
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [statusLoading, setStatusLoading] = useState(false);
  const [unassignLoading, setUnassignLoading] = useState(false);
  // Cancel actions are handled by DonationCancelConfirmationModal; loading
  // stays false here so FoodRunInfo button props remain stable.
  const cancelLoadingInstance = false;
  const cancelLoadingBulk = false;

  const sendSocketEvent = () => {
    socket.emit('DONATION-UPDATE', {
      patternId: data.parent_id,
      teamId: user.team_id,
      type: 'FOOD-RUN',
      donationId: data.id,
    });
  };

  const handleUnAssignRunner = () => {
    setUnassignLoading(true);
    donations
      .unassignSingleFoodInstance({
        rescueId: data.rescue_id,
        parentId: data.parent_id,
        donationId: data.id,
      })
      .then(getFoodRunData)
      .then(sendSocketEvent)
      .finally(() => setUnassignLoading(false));
  };

  const handleBeginPickup = () => {
    updateStatus('PICKUP_IN_PROGRESS');
  };

  const handleBeginDelivery = () => {
    updateStatus('DELIVERY_IN_PROGRESS');
  };

  const updateStatus = (status) => {
    setStatusLoading(true);
    donations
      .updateFoodRunStatus({
        status,
        rescueId: data.rescue_id,
        donationId: data.id,
      })
      .then(getFoodRunData)
      .then(sendSocketEvent)
      .catch((e) => enqueueSnackbar(e.message, { variant: 'error' }))
      .finally(() => setStatusLoading(false));
  };

  const handleClaim = ({ drivers = [] } = {}) => {
    setStatusLoading(true);
    donations
      .assignFoodRun({
        donationId: data.id,
        drivers,
      })
      .then(getFoodRunData)
      .then(sendSocketEvent)
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
      .finally(() => setStatusLoading(false));
  };

  const handleReAssignRescue = ({ drivers = [] }) => {
    setStatusLoading(true);
    donations
      .updateDriversForFoodRun({
        rescueId: data.rescue_id,
        donationId: data.id,
        drivers,
      })
      .then(getFoodRunData)
      .then(sendSocketEvent)
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
      .finally(() => setStatusLoading(false));
  };

  const handleComplete = async (completeData) => {
    setStatusLoading(true);
    let rescueId = data.rescue_id;
    if (data.food_run_status !== 'COMPLETED' && !rescueId) {
      const res = await rescues.assignDriverToIncompleteDonation({
        donationId: data.id,
        drivers: [{ user_id: user.id }],
      });
      rescueId = res.rescueId;
    }
    foodRun
      .completeFoodRun({
        ...completeData,
        rescueId,
        donorTeam: data.donor_team,
        receiverTeam: data.receiver_team,
      })
      .then(handleCompleteSuccess)
      .then(sendSocketEvent)
      .catch((err) => enqueueSnackbar(err.message, { variant: 'error' }))
      .finally(() => setStatusLoading(false));
  };

  // Full cancels require a reason (enforced server-side). Callers must use
  // DonationCancelConfirmationModal rather than these stubs.
  const handleCancelFoodRunSingleInstance = () => {
    enqueueSnackbar(
      'A cancellation reason is required. Use the cancel dialog to select one.',
      { variant: 'warning' }
    );
  };

  const handleCancelFoodRun = () => {
    enqueueSnackbar(
      'A cancellation reason is required. Use the cancel dialog to select one.',
      { variant: 'warning' }
    );
  };
  return {
    statusLoading,
    unassignLoading,
    cancelLoadingInstance,
    cancelLoadingBulk,
    handleUnAssignRunner: debouncedFunction(handleUnAssignRunner),
    handleBeginDelivery: debouncedFunction(handleBeginDelivery),
    handleBeginPickup: debouncedFunction(handleBeginPickup),
    handleClaim: debouncedFunction(handleClaim),
    handleComplete: debouncedFunction(handleComplete),
    handleCancelFoodRunSingleInstance: debouncedFunction(
      handleCancelFoodRunSingleInstance
    ),
    handleCancelFoodRun: debouncedFunction(handleCancelFoodRun),
    handleReAssignRescue: debouncedFunction(handleReAssignRescue),
    rescueId: data.rescue_id,
  };
}
